import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { DiscountCodePreviewView } from "@keydials/api-client";
import { api } from "../auth/AuthContext";
import { discountLabel, normalise, readCode } from "../discountCode";

const NOT_VALID = "That discount code isn't valid for this plan.";

/**
 * "Have you got a discount code?" (KEY-642) — on the buy page, a campaign sign-up, the trial-ended screen
 * and the Subscription card.
 *
 * Prefilled from `?code=` on the page's own URL and checked straight away, so a launch email's link lands
 * with the discount already showing. Checked again whenever the plan changes: a code limited to one
 * family should not keep saying "20% off" beside a plan it does not cover.
 *
 * What the parent sends is whatever is in the box, applied or not — never silently nothing. A code typed
 * but not applied still goes to the server, which refuses the checkout if it does not apply, so nobody
 * who typed LAUNCH20 is charged full price because they skipped a button.
 */
export function DiscountCodeField({
  planId,
  period,
  fullPricePence,
  disabled,
  onChange,
  initialCode,
}: {
  /** The plan the code is for. Null while none is chosen — Apply waits for one. */
  planId: string | null;
  /** The plan's billing period, which decides how the discount's length is worded. */
  period: string;
  /** The plan's price, so an applied code can say what is paid once the discount ends. */
  fullPricePence?: number | null;
  disabled?: boolean;
  /** The code to send with the checkout, or null for none. Must be stable (a state setter is ideal). */
  onChange: (code: string | null) => void;
  /**
   * A code to start with when the link carried none — an affiliate's buyer discount (KEY-514). Read once, on
   * mount, like the link's own; remount the field (a `key`) to apply one that arrives later.
   */
  initialCode?: string | null;
}) {
  const inputId = useId();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const linked = useMemo(() => readCode(window.location.search) || normalise(initialCode) || "", []);
  const [open, setOpen] = useState(linked !== "");
  const [draft, setDraft] = useState(linked);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [preview, setPreview] = useState<DiscountCodePreviewView | null>(null);
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  /** The code last checked, so a plan change re-checks it — but not a half-typed edit made since. */
  const checked = useRef<string | null>(linked || null);
  /** Only the latest check may land; switching plan mid-check must not show the old plan's answer. */
  const latest = useRef(0);

  useEffect(() => {
    onChange(draft.trim() || null);
  }, [draft, onChange]);

  const apply = useCallback(
    async (raw: string) => {
      if (!planId) return;
      const request = ++latest.current;
      const code = normalise(raw);
      checked.current = raw;
      if (!code) {
        setPreview(null);
        setProblem(NOT_VALID);
        return;
      }
      setChecking(true);
      setProblem(null);
      try {
        const found = await api.previewDiscountCode(code, planId);
        if (request !== latest.current) return;
        setPreview(found);
        setProblem(found ? null : NOT_VALID);
        if (found) {
          setDraft(found.code);
          checked.current = found.code;
        }
      } catch (e) {
        if (request !== latest.current) return;
        setPreview(null);
        setProblem(e instanceof Error && e.message ? e.message : "Couldn't check that code.");
      } finally {
        if (request === latest.current) setChecking(false);
      }
    },
    [planId],
  );

  // The link's code on arrival, and an already-checked code whenever the plan changes.
  useEffect(() => {
    const code = checked.current;
    if (planId && code && code === draftRef.current) void apply(code);
  }, [planId, apply]);

  const remove = () => {
    latest.current++;
    checked.current = null;
    setDraft("");
    setPreview(null);
    setProblem(null);
    setChecking(false);
    setOpen(false);
  };

  if (!open) {
    return (
      <button type="button" className="discount-toggle" disabled={disabled} onClick={() => setOpen(true)}>
        Have you got a discount code?
      </button>
    );
  }

  return (
    <div className="discount-code">
      <label htmlFor={inputId}>Discount code</label>
      <div className="discount-code-row">
        <input
          id={inputId}
          type="text"
          value={draft}
          maxLength={40}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="Enter your code"
          disabled={disabled}
          onChange={(e) => {
            setDraft(e.target.value);
            setPreview(null);
            setProblem(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void apply(draft);
            }
          }}
        />
        <button
          type="button"
          className="discount-code-apply"
          disabled={disabled || checking || !draft.trim() || !planId}
          onClick={() => void apply(draft)}
        >
          {checking ? "Checking…" : "Apply"}
        </button>
      </div>
      {preview && (
        <p className="discount-code-applied" role="status">
          <span>
            <span aria-hidden>✓ </span>
            <strong>{preview.code}</strong>: {discountLabel(preview, period, fullPricePence)}
          </span>
          <button type="button" className="discount-code-remove" disabled={disabled} onClick={remove}>
            Remove
          </button>
        </p>
      )}
      {problem && (
        <p className="discount-code-problem" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
